import { v4 as uuid4 } from 'uuid';
import JSZip from 'jszip';
import * as Papa from 'papaparse';
import { Point, Line } from '../../../context/context';
import { IconType } from '../../../context/context';

interface GTFSData {
    stops: any[];
    routes: any[];
    trips: any[];
    shapes: any[];
    stopTimes: any[];
}

/**
 * Parse CSV content using PapaParse
 */
const parseCSV = async (content: string): Promise<any[]> => {
    return new Promise((resolve, reject) => {
        Papa.parse<any>(content, {
            header: true,
            complete: (results) => resolve(results.data),
            error: (error: Error) => reject(error)
        });
    });
};

/**
 * Process GTFS data into Points and Lines
 */
export const processGTFSData = async (data: GTFSData) => {
    const points: Point[] = [];
    const lines: Line[] = [];

    // Process stops as points
    data.stops.forEach((stop: any) => {
        if (stop.stop_lat && stop.stop_lon) {
            const point: Point = {
                id: uuid4(),
                type: 'point',
                name: stop.stop_name || 'Stop',
                description: stop.stop_desc || '',
                colour: '#FF0000',
                icon: 'location' as IconType,
                size: 24,
                isLocked: false,
                coordinates: [parseFloat(stop.stop_lat), parseFloat(stop.stop_lon)],
            };
            points.push(point);
        }
    });

    // Process shapes as lines
    const shapesByRoute: { [key: string]: any[] } = {};
    data.shapes.forEach((shape: any) => {
        if (!shapesByRoute[shape.shape_id]) {
            shapesByRoute[shape.shape_id] = [];
        }
        shapesByRoute[shape.shape_id].push(shape);
    });

    Object.entries(shapesByRoute).forEach(([shapeId, shapes]) => {
        const sortedShapes = shapes.sort((a, b) => 
            parseInt(a.shape_pt_sequence) - parseInt(b.shape_pt_sequence)
        );

        const trip = data.trips.find((t: any) => t.shape_id === shapeId);
        if (trip) {
            const route = data.routes.find((r: any) => r.route_id === trip.route_id);
            if (route) {
                const coordinates: [number, number][] = sortedShapes
                    .map((shape: any) => [
                        parseFloat(shape.shape_pt_lat),
                        parseFloat(shape.shape_pt_lon)
                    ])
                    .filter((coord: number[]) => coord.length === 2 && !coord.some(isNaN)) as [number, number][];

                if (coordinates.length > 1) {
                    const line: Line = {
                        id: uuid4(),
                        type: 'line',
                        name: route.route_short_name || route.route_long_name || 'Route',
                        description: route.route_desc || '',
                        colour: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
                        opacity: '0.8',
                        borderColour: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
                        borderWidth: 2,
                        isLocked: false,
                        coordinates,
                        arrowStyle: 'arrow',
                    };
                    lines.push(line);
                }
            }
        }
    });

    return { points, lines };
};

export const processGTFSFile = async (file: File): Promise<{ points: Point[], lines: Line[] }> => {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(file);
    
    const requiredFiles = ['stops.txt', 'routes.txt', 'trips.txt', 'shapes.txt', 'stop_times.txt'];
    const missingFiles = requiredFiles.filter(file => !zipContent.files[file]);
    
    if (missingFiles.length > 0) {
        throw new Error(`Missing required GTFS files: ${missingFiles.join(', ')}`);
    }

    const data: GTFSData = {
        stops: [],
        routes: [],
        trips: [],
        shapes: [],
        stopTimes: [],
    };

    for (const file of requiredFiles) {
        const content = await zipContent.files[file].async('text');
        const parsed = await parseCSV(content);
        
        switch (file) {
            case 'stops.txt':
                data.stops = parsed;
                break;
            case 'routes.txt':
                data.routes = parsed;
                break;
            case 'trips.txt':
                data.trips = parsed;
                break;
            case 'shapes.txt':
                data.shapes = parsed;
                break;
            case 'stop_times.txt':
                data.stopTimes = parsed;
                break;
        }
    }

    return processGTFSData(data);
}; 